import { ArrowDown, ArrowUp, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import { useSaveBarEntry } from '@nucleus-commerce/admin-kit/save-bar';
import type { CollectionProductItem, ProductSelection } from '@nucleus-commerce/api-client/admin';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';

import { selectionChanged } from '@/shared/bulk/selection-errors';
import { useBulkErrorMessage } from '@/shared/bulk/use-bulk-error-message';
import { useSelectionRequest } from '@/shared/bulk/use-selection-request';

import {
  useAddCollectionProducts,
  useRemoveCollectionProduct,
  useSetCollectionProducts,
} from '../hooks/use-collection-mutations';

import { AddCollectionProductsDialog } from './add-collection-products-dialog';
import { CollectionProductRow } from './collection-product-row';

/** The backend's MAX_COLLECTION_PRODUCTS. */
export const MAX_COLLECTION_PRODUCTS = 2000;

export interface ManualProductsSectionProps {
  storeId: string;
  collectionId: string;
  /** The saved list, in display order. */
  items: readonly CollectionProductItem[];
  canWrite: boolean;
}

const ids = (rows: readonly CollectionProductItem[]) => rows.map((r) => r.productId).join('\n');

const renumber = (rows: CollectionProductItem[]) => rows.map((r, i) => ({ ...r, position: i }));

function move(rows: CollectionProductItem[], from: number, to: number): CollectionProductItem[] {
  if (to < 0 || to >= rows.length) return rows;
  const next = [...rows];
  const [row] = next.splice(from, 1);
  if (row) next.splice(to, 0, row);
  return renumber(next);
}

/**
 * The local order rebased on a new server list: rows the server dropped go, rows it added are
 * appended, and an unsaved reorder of the rest survives.
 */
function rebase(
  local: readonly CollectionProductItem[],
  saved: readonly CollectionProductItem[],
): CollectionProductItem[] {
  const byId = new Map(saved.map((r) => [r.productId, r]));
  const kept = local.flatMap((r) => {
    const fresh = byId.get(r.productId);
    return fresh ? [fresh] : [];
  });
  const known = new Set(kept.map((r) => r.productId));
  return renumber([...kept, ...saved.filter((r) => !known.has(r.productId))]);
}

/** Products refused because they are not in the store, read from the 422's meta. */
function refusedIds(error: unknown): string[] {
  if (!isApiError(error) || error.code !== 'INVALID_REFERENCE') return [];
  const list = error.meta.productIds;
  return Array.isArray(list) ? list.filter((id): id is string => typeof id === 'string') : [];
}

/**
 * Ordered manual list. Adding and removing a product take effect at once (POST and DELETE on the
 * list); a new order is kept locally until "Save order" sends it with one PUT.
 */
export function ManualProductsSection({
  storeId,
  collectionId,
  items,
  canWrite,
}: ManualProductsSectionProps) {
  const { t } = useTranslation(['collections', 'common', 'errors']);
  const format = useFormat();
  const message = useBulkErrorMessage();
  const selectionRequest = useSelectionRequest();
  const saveOrder = useSetCollectionProducts(storeId);
  const addProducts = useAddCollectionProducts(storeId);
  const removeProduct = useRemoveCollectionProduct(storeId);
  const [rows, setRows] = useState<CollectionProductItem[]>([...items]);
  const [baseline, setBaseline] = useState(items);
  const [seen, setSeen] = useState(items);
  const [removingId, setRemovingId] = useState<string | null>(null);
  if (seen !== items) {
    setSeen(items);
    setBaseline(items);
    setRows(rebase(rows, items));
  }

  const dirty = ids(rows) !== ids(baseline);
  const busy = saveOrder.isPending || addProducts.isPending || removeProduct.isPending;
  const atCap = rows.length >= MAX_COLLECTION_PRODUCTS;
  const failure = saveOrder.error ?? addProducts.error ?? removeProduct.error;
  // A changed selection is asked about in its own dialog, not shown here.
  const error = selectionChanged(failure) ? null : failure;
  const refused = refusedIds(error);

  const adopt = (saved: readonly CollectionProductItem[]) => {
    setBaseline(saved);
    setRows((current) => rebase(current, saved));
  };

  const add = async (selection: ProductSelection) => {
    const known = new Set(rows.map((r) => r.productId));
    const picked: ProductSelection =
      selection.mode === 'ids'
        ? { mode: 'ids', ids: selection.ids.filter((id) => !known.has(id)) }
        : selection;
    if (picked.mode === 'ids' && picked.ids.length === 0) return;
    saveOrder.reset();
    removeProduct.reset();
    try {
      const saved = await selectionRequest.run(picked, (body) =>
        addProducts.mutateAsync({ id: collectionId, selection: body }),
      );
      if (saved === null) addProducts.reset();
      else adopt(saved);
    } catch {
      // The alert above the list shows the error.
    }
  };

  const remove = (productId: string) => {
    saveOrder.reset();
    addProducts.reset();
    setRemovingId(productId);
    removeProduct.mutate(
      { id: collectionId, productId },
      {
        onSuccess: () => {
          setBaseline((current) => renumber(current.filter((r) => r.productId !== productId)));
          setRows((current) => renumber(current.filter((r) => r.productId !== productId)));
        },
        onSettled: () => setRemovingId(null),
      },
    );
  };

  const saveProductOrder = () =>
    saveOrder.mutate(
      { id: collectionId, productIds: rows.map((r) => r.productId) },
      { onSuccess: adopt },
    );

  const discardOrder = () => setRows([...baseline]);

  // A pending reorder sits under the global save bar with the collection form; Save sends the PUT.
  useSaveBarEntry({
    dirty,
    saving: saveOrder.isPending,
    onSave: saveProductOrder,
    onDiscard: discardOrder,
    disabled: !canWrite,
  });

  return (
    <FormSection
      title={t('collections:sections.products')}
      description={t('collections:sections.productsDescription')}
      tour="collections.edit.manual-products"
    >
      <p className="text-muted-foreground text-xs">
        {t('collections:products.capacity', {
          count: rows.length,
          formatted: format.number(rows.length),
          max: format.number(MAX_COLLECTION_PRODUCTS),
        })}
      </p>
      {canWrite ? (
        <div className="flex flex-wrap items-center gap-2">
          <AddCollectionProductsDialog
            storeId={storeId}
            collectionId={collectionId}
            excludeIds={new Set(rows.map((r) => r.productId))}
            remaining={MAX_COLLECTION_PRODUCTS - rows.length}
            onAdd={(selection) => void add(selection)}
            disabled={busy || atCap}
          />
          {selectionRequest.dialog}
          <span className="flex-1" />
          <Button type="button" size="sm" disabled={!dirty || busy} onClick={saveProductOrder}>
            {saveOrder.isPending
              ? t('collections:products.saving')
              : t('collections:products.saveOrder')}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={!dirty || busy}
            onClick={discardOrder}
          >
            {t('collections:actions.discard')}
          </Button>
        </div>
      ) : null}
      {canWrite && atCap ? (
        <p className="text-muted-foreground text-xs">
          {t('collections:products.full', { max: format.number(MAX_COLLECTION_PRODUCTS) })}
        </p>
      ) : null}
      {error ? (
        <Alert variant="destructive" role="alert">
          <AlertDescription>
            {refused.length > 0
              ? t('collections:products.notInStore', { count: refused.length })
              : message(error)}
            {isApiError(error) && error.requestId ? (
              <span className="block text-xs">
                {t('errors:requestIdShort', { requestId: error.requestId })}
              </span>
            ) : null}
          </AlertDescription>
        </Alert>
      ) : null}
      {rows.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t('collections:products.empty')}</p>
      ) : (
        <ol className="divide-y rounded-md border" aria-label={t('collections:sections.products')}>
          {rows.map((item, index) => (
            <CollectionProductRow
              key={item.productId}
              item={item}
              className={refused.includes(item.productId) ? 'bg-destructive/5' : undefined}
              actions={
                canWrite ? (
                  <span className="flex shrink-0 items-center">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      disabled={index === 0 || busy}
                      onClick={() => setRows((current) => move(current, index, index - 1))}
                      aria-label={t('collections:products.moveUp', { title: item.title })}
                    >
                      <ArrowUp aria-hidden />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      disabled={index === rows.length - 1 || busy}
                      onClick={() => setRows((current) => move(current, index, index + 1))}
                      aria-label={t('collections:products.moveDown', { title: item.title })}
                    >
                      <ArrowDown aria-hidden />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      disabled={busy}
                      aria-busy={removingId === item.productId}
                      onClick={() => remove(item.productId)}
                      aria-label={t('collections:products.remove', { title: item.title })}
                    >
                      <X aria-hidden />
                    </Button>
                  </span>
                ) : null
              }
            />
          ))}
        </ol>
      )}
    </FormSection>
  );
}
